import React, { useState } from 'react';
import EmailInput from '../EmailInput';
import { checkEmail } from '../../utils/checkEmail';

// Email step in the AI onboarding chat: email, terms + 18+ (required — this
// also covers emails about new matches, which are part of the service), and an
// unticked choice to have the account deleted in 3 months if no suggestion
// interests them (the same pending deletion as Settings' "delete in 30 days").
// Once submitted, `submitted` holds what they sent and the form stays in the
// chat, greyed out and read-only.
const EmailConsentForm = ({ onSubmit, submitted }) => {
  const [email, setEmail] = useState(submitted?.email || '');
  const [acceptTerms, setAcceptTerms] = useState(!!submitted);
  const [deleteIfInactive, setDeleteIfInactive] = useState(!!submitted?.deleteIfInactive);
  const [showWhy, setShowWhy] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const locked = busy || !!submitted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!acceptTerms) {
      setError('Please accept the terms and confirm you are 18 or over.');
      return;
    }
    setError('');
    setBusy(true);
    const emailOk = await checkEmail(email);
    if (!emailOk.ok) {
      setBusy(false);
      setError(emailOk.reason);
      return;
    }
    const result = await onSubmit({ email: email.trim(), acceptTerms: true, confirmAge: true, notifyConsent: true, deleteIfInactive });
    setBusy(false);
    if (result?.error) setError(result.error);
  };

  return (
    <form className={`aio-form${submitted ? ' aio-form--done' : ''}`} onSubmit={handleSubmit}>
      <label className="aio-label" htmlFor="aio-email">Your email</label>
      <EmailInput
        id="aio-email"
        className="aio-input"
        placeholder="you@example.com"
        value={email}
        onChange={setEmail}
        disabled={locked}
      />

      <label className="aio-check">
        <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} disabled={locked} />
        <span>
          I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, I'm 18 or over, and I'm happy for you to email me suggested members.
        </span>
      </label>


      <label className="aio-check">
        <input type="checkbox" checked={deleteIfInactive} onChange={(e) => setDeleteIfInactive(e.target.checked)} disabled={locked} />
        <span>
          Delete my account after 3 months if I receive no suggestions I'm interested in.{' '}
          <button type="button" className="aio-link-btn aio-inline-link" onClick={() => setShowWhy(!showWhy)}>
            {showWhy ? 'Hide' : 'Why?'}
          </button>
        </span>
      </label>

      {showWhy && (
        <div className="aio-why">
          <p>Reasons to wait 3 months:</p>
          <ul>
            <li>We suggest on quality, not quantity.</li>
            <li>We won't bombard you with suggestions, only when there's truly a good fit.</li>
            <li>This group is new, and people are joining all the time.</li>
            <li>If someone joins who is a good fit, we'll let you know.</li>
            <li>It's very likely someone like you will join in a few weeks — but if you leave too soon, you'd be two passing ships in the night!</li>
          </ul>
        </div>
      )}

      {error && <div className="aio-error">{error}</div>}

      <button className="demo-signup-button" type="submit" disabled={locked}>
        {submitted ? '✓ Sent' : busy ? 'Saving…' : 'Continue'}
      </button>
    </form>
  );
};

export default EmailConsentForm;
